import { useTranslation } from "react-i18next";
import { getComplianceCoreUrl } from "@/config/domains";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import LocaleLink from "@/components/LocaleLink";
import { SiteSearchTrigger } from "@/components/SiteSearch";
import { usePlatformMetrics } from '@/hooks/usePlatformMetrics';

/**
 * The left slot used to hold a brand switcher with exactly one brand in it, so
 * on csquaregrc.com it rendered the words "cSquare GRC" as a link to the page
 * you were already on, eight pixels above a logo saying the same thing.
 *
 * It carries the coverage figures instead. A utility strip is where standing
 * facts belong; the right cluster already has the actions. Both numbers come
 * from platform-metrics.ts with the query that verifies them, and the second
 * is the one a competitor cannot fake.
 */
const BrandBar = () => {
  const m = usePlatformMetrics();
  const { t } = useTranslation();

  return (
    <div className="w-full border-b border-white/10 text-xs">
      {/* Below md the coverage line is hidden, so the utility items are the only
          thing in the row. Left as justify-between they hugged the right edge
          with dead space beside them, which reads like something failed to
          load. Centred, the row looks deliberate. */}
      {/* h-6 is 24px, the WCAG 2.2 SC 2.5.8 minimum. Every control in the row
          fills that height (min-h-6 + inline-flex) so the hit target is the
          whole strip, not just the 12px text box. Do not shrink this row. */}
      <div className="container mx-auto flex h-6 items-center justify-center px-4 md:justify-between lg:px-6">
        <div className="hidden items-center md:flex">
          <LocaleLink
            to="/templates"
            className="inline-flex items-center min-h-6 font-medium text-white/55 transition-colors hover:text-white/90"
          >
            {t("navigation.coverageStat", {
              frameworks: m.frameworks_in_picker,
              cited: m.cited_requirements.toLocaleString("en-US"),
              })}
          </LocaleLink>
        </div>
        <div className="flex items-center gap-3">
          {/* Search sits with the utility items rather than in the primary nav:
              it is a way through the site, not a destination in it. Cmd/Ctrl-K
              opens the same dialog. */}
          <SiteSearchTrigger />
          <span className="text-white/25 select-none" aria-hidden>·</span>
          <LocaleLink
            to="/contact"
            className="inline-flex items-center min-h-6 text-white/55 hover:text-white/90 font-medium transition-colors"
          >
            {t("navigation.contactSales")}
          </LocaleLink>
          <span className="text-white/25 select-none" aria-hidden>·</span>
          <a
            href={getComplianceCoreUrl("/auth")}
            className="inline-flex items-center min-h-6 text-white/55 hover:text-white/90 font-medium transition-colors"
          >
            {t("navigation.signIn")}
          </a>
          <span className="text-white/25 select-none" aria-hidden>·</span>
          <LocaleSwitcher />
        </div>
      </div>
    </div>
  );
};

export default BrandBar;
